import type { OrderItem } from '../../../types/apiTypes';
import type { SplitOrderBoxLine } from '../../../services/shipStationAPI';
import { normalizeSku } from '../../../utils/normalizeSku';
import { consolidatePickLines } from './ComponentPickList';
import { pickLineUnitCount } from './orderDataLogic';

export const MAX_SPLIT_BOXES = 4;
export const INITIAL_SPLIT_BOXES = 2;

/** Units of each split line (keyed by `SplitLine.id`) packed in one box. A bundle line's unit is one whole bundle. */
export type SplitBox = Readonly<Record<string, number>>;
export type SplitBoxes = readonly SplitBox[];

export const SPLIT_LINE_KINDS = {
  ITEM: 'item',
  BUNDLE: 'bundle',
} as const;

export interface SplitBundleComponent {
  readonly sku: string;
  readonly name: string;
  /** Units of this SKU in one bundle. */
  readonly quantityPerBundle: number;
}

interface SplitLineBase {
  readonly id: string;
  readonly sku: string;
  readonly name: string;
  readonly quantity: number;
}

export interface SplitItemLine extends SplitLineBase {
  readonly kind: typeof SPLIT_LINE_KINDS.ITEM;
}

export interface SplitBundleLine extends SplitLineBase {
  readonly kind: typeof SPLIT_LINE_KINDS.BUNDLE;
  readonly components: readonly SplitBundleComponent[];
}

export type SplitLine = SplitItemLine | SplitBundleLine;

interface BundleGroup {
  readonly parent: OrderItem;
  readonly components: readonly SplitBundleComponent[];
  count: number;
}

function bundleComponentsOf(parentId: string, orderItems: readonly OrderItem[]): SplitBundleComponent[] {
  const bySku = new Map<string, { sku: string; name: string; quantityPerBundle: number }>();
  orderItems.forEach((item) => {
    if (!item.isComponent || item.parentId !== parentId) return;
    const key = normalizeSku(item.sku);
    const existing = bySku.get(key);
    if (existing) {
      existing.quantityPerBundle += pickLineUnitCount(item);
    } else {
      bySku.set(key, { sku: item.sku, name: item.name, quantityPerBundle: pickLineUnitCount(item) });
    }
  });
  return [...bySku.values()];
}

function bundleSignature(parentSku: string, components: readonly SplitBundleComponent[]): string {
  const parts = components
    .map((component) => `${normalizeSku(component.sku)}:${component.quantityPerBundle}`)
    .sort();
  return [normalizeSku(parentSku), ...parts].join('|');
}

/**
 * Standalone pick lines merged per SKU, then one line per distinct bundle (same parent SKU and
 * same component makeup) with `quantity` = bundle count. Components stay nested under their bundle.
 */
export function buildSplitLines(orderItems: readonly OrderItem[]): SplitLine[] {
  const parents = orderItems.filter((item) => item.isBundleParent === true);
  const parentIds = new Set(parents.map((parent) => parent.id));
  const standalone = orderItems.filter(
    (item) => item.isBundleParent !== true && !(item.isComponent && item.parentId != null && parentIds.has(item.parentId)),
  );

  const itemLines = consolidatePickLines(standalone).map((item): SplitItemLine => ({
    kind: SPLIT_LINE_KINDS.ITEM,
    id: item.id,
    sku: item.sku,
    name: item.name,
    quantity: item.totalQty ?? 1,
  }));

  const groups = new Map<string, BundleGroup>();
  parents.forEach((parent) => {
    const components = bundleComponentsOf(parent.id, orderItems);
    const signature = bundleSignature(parent.sku, components);
    const group = groups.get(signature);
    if (group) {
      group.count += 1;
    } else {
      groups.set(signature, { parent, components, count: 1 });
    }
  });

  const bundleLines = [...groups.values()].map(({ parent, components, count }): SplitBundleLine => ({
    kind: SPLIT_LINE_KINDS.BUNDLE,
    id: `bundle-${parent.id}`,
    sku: parent.sku,
    name: parent.name,
    quantity: count,
    components,
  }));

  return [...itemLines, ...bundleLines];
}

/** Every line in Box 1, plus empty boxes up to `INITIAL_SPLIT_BOXES`. */
export function initialSplitBoxes(lines: readonly SplitLine[]): SplitBoxes {
  const first: Record<string, number> = {};
  lines.forEach((line) => {
    first[line.id] = line.quantity;
  });
  return [first, ...Array.from({ length: INITIAL_SPLIT_BOXES - 1 }, (): SplitBox => ({}))];
}

/** Box a line moves to: the next box, wrapping back to Box 1. */
export function nextBoxIndex(current: number, boxCount: number): number {
  if (boxCount <= 1) return 0;
  return (current + 1) % boxCount;
}

/** Moves `qty` units of a line between boxes; units already in the target box are combined. */
function transferUnits(boxes: SplitBoxes, from: number, to: number, itemId: string, qty: number): SplitBoxes {
  const available = boxes[from]?.[itemId] ?? 0;
  const units = Math.min(qty, available);
  if (from === to || units <= 0) return boxes;
  return boxes.map((box, index) => {
    if (index === from) {
      const { [itemId]: current = 0, ...rest } = box;
      const remaining = current - units;
      return remaining > 0 ? { ...rest, [itemId]: remaining } : rest;
    }
    if (index === to) {
      return { ...box, [itemId]: (box[itemId] ?? 0) + units };
    }
    return box;
  });
}

/** Tap: the whole line moves to the next box. */
export function moveLineToNextBox(boxes: SplitBoxes, from: number, itemId: string): SplitBoxes {
  return transferUnits(boxes, from, nextBoxIndex(from, boxes.length), itemId, boxes[from]?.[itemId] ?? 0);
}

/** Hold: half the units (rounded down) move to the next box. A single unit stays put. */
export function splitLineToNextBox(boxes: SplitBoxes, from: number, itemId: string): SplitBoxes {
  const half = Math.floor((boxes[from]?.[itemId] ?? 0) / 2);
  return transferUnits(boxes, from, nextBoxIndex(from, boxes.length), itemId, half);
}

/** Split Quantity modal: `qty` units move to the next box. */
export function moveUnitsToNextBox(boxes: SplitBoxes, from: number, itemId: string, qty: number): SplitBoxes {
  return transferUnits(boxes, from, nextBoxIndex(from, boxes.length), itemId, qty);
}

/** What a chip hold does: split half, or open the Split Quantity modal. */
export const SPLIT_HOLD_MODES = {
  HALF: 'half',
  QUANTITY: 'quantity',
} as const;
export type SplitHoldMode = (typeof SPLIT_HOLD_MODES)[keyof typeof SPLIT_HOLD_MODES];

export type SplitQuantityField = 'stay' | 'move';

export interface LinkedSplitQuantity {
  readonly stay: number;
  readonly move: number;
}

/** Either field sets the other so they always sum to `total`. Over `total`, the other side goes negative. */
export function linkSplitQuantity(total: number, field: SplitQuantityField, raw: string): LinkedSplitQuantity | null {
  if (!/^\d+$/.test(raw)) return null;
  const value = Number.parseInt(raw, 10);
  if (!Number.isSafeInteger(value)) return null;
  return field === 'stay' ? { stay: value, move: total - value } : { stay: total - value, move: value };
}

export function isValidSplitQuantity(linked: LinkedSplitQuantity): boolean {
  return linked.stay >= 0 && linked.move >= 0;
}

export function addSplitBox(boxes: SplitBoxes): SplitBoxes {
  return boxes.length >= MAX_SPLIT_BOXES ? boxes : [...boxes, {}];
}

/** Removes a box and combines its lines into the box before it. Box 1 cannot be closed. */
export function closeSplitBox(boxes: SplitBoxes, index: number): SplitBoxes {
  if (index <= 0 || index >= boxes.length) return boxes;
  const merged: Record<string, number> = { ...boxes[index - 1] };
  Object.entries(boxes[index]).forEach(([itemId, qty]) => {
    merged[itemId] = (merged[itemId] ?? 0) + qty;
  });
  return boxes.flatMap((box, boxIndex) => {
    if (boxIndex === index) return [];
    return [boxIndex === index - 1 ? merged : box];
  });
}

/**
 * Split lines to ShipStation SKU quantities per box. A bundle expands to its component SKUs (the
 * ShipStation lines); a SKU that appears more than once in a box is summed.
 */
export function splitBoxesToRequest(
  lines: readonly SplitLine[],
  boxes: SplitBoxes,
): SplitOrderBoxLine[][] {
  const lineById = new Map(lines.map((line) => [line.id, line]));
  return boxes.map((box) => {
    const bySku = new Map<string, number>();
    const add = (sku: string, quantity: number): void => {
      bySku.set(sku, (bySku.get(sku) ?? 0) + quantity);
    };
    Object.entries(box).forEach(([lineId, quantity]) => {
      const line = lineById.get(lineId);
      if (!line || quantity <= 0) return;
      if (line.kind === SPLIT_LINE_KINDS.BUNDLE) {
        line.components.forEach((component) => add(component.sku, component.quantityPerBundle * quantity));
      } else {
        add(line.sku, quantity);
      }
    });
    return [...bySku].map(([sku, quantity]) => ({ sku, quantity }));
  });
}

/** At least two boxes, and none empty. */
export function canConfirmSplit(boxes: SplitBoxes): boolean {
  return boxes.length > 1 && boxes.every((box) => Object.keys(box).length > 0);
}
